import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { environment } from '../../../../../environments/environment';
import { CommonService } from '../../common-service';
import { ContasReceberGetAllRequest } from '../../requests/financas/contas-receber-get-all.request';
import { ContasReceberGetAllResponse } from '../../responses/financas/contas-receber-get-all.response';
import { ContasReceberBulkRequest } from '../../requests/financas/contas-receber-bulk.request';
import { ContasReceberBulkAlterarFormaRecebimentoRequest } from '../../requests/financas/contas-receber-bulk-alterar-forma-recebimento.request';
import { MarcarPerdaContasReceberRequest } from '../../requests/financas/marcar-perda-contas-receber.request';
import { ReverterPerdaContasReceberRequest } from '../../requests/financas/reverter-perda-contas-receber.request';
import { ContasReceberBulkCategoriaRequest, ContasReceberBulkEditRequest, ContasReceberBulkTagsRequest } from '../../requests/financas/contas-receber.request';
import { GetLookupsContasReceberResponse } from '../../responses/financas/get-lookups-contas-receber.response';
import { ReturnStruct } from '../../structs/return.struct';
import { ContasReceberBulkDeleteResponse } from '../../responses/financas/contas-receber-bulk-delete.response';
import { EstornoPixInfoResponse } from '../../responses/financas/estorno-pix-info.response';
import { CobrancaAtivoInfoResponse } from '../../responses/financas/cobranca-ativo-info.response';

@Injectable({
    providedIn: 'root',
})
export class ContasReceberService extends CommonService {
    private readonly apiUrl: string = environment.urlApiFinancas + 'ContasReceber';

    constructor(private httpClient: HttpClient) {
        super();
    }

    public getLookups(): Observable<GetLookupsContasReceberResponse> {
        const url = `${this.apiUrl}/lookups`;
        return this.httpClient
            .get<GetLookupsContasReceberResponse>(url, this.addHeaderToken())
            .pipe(catchError(this.handleError.bind(this)));
    }

    public getAll(request: ContasReceberGetAllRequest, pagina: number = 0, tamanhoPagina: number = 20): Observable<ContasReceberGetAllResponse> {
        const params = new HttpParams()
            .set('pagina', pagina.toString())
            .set('tamanhoPagina', tamanhoPagina.toString());

        const url = `${this.apiUrl}/getAll`;

        return this.httpClient
            .post<ContasReceberGetAllResponse>(url, request, { params, ...this.addHeaderToken() })
            .pipe(catchError(this.handleError));
    }

    public getPrint(request: ContasReceberGetAllRequest): Observable<ContasReceberGetAllResponse> {
        const url = `${this.apiUrl}/print`;
        return this.httpClient
            .post<ContasReceberGetAllResponse>(url, request, this.addHeaderToken())
            .pipe(catchError(this.handleError));
    }

    public getById(id: number): Observable<any> {
        const url = `${this.apiUrl}/idContasReceber/${id}`;
        return this.httpClient
            .get<any>(url, this.addHeaderToken())
            .pipe(catchError(this.handleError));
    }

    public create(request: any): Observable<ReturnStruct> {
        return this.httpClient
            .post<ReturnStruct>(this.apiUrl, request, this.addHeaderToken())
            .pipe(catchError(this.handleError));
    }

    public update(request: any): Observable<ContasReceberBulkDeleteResponse> {
        return this.httpClient
            .put<ContasReceberBulkDeleteResponse>(this.apiUrl, request, this.addHeaderToken())
            .pipe(catchError(this.handleError));
    }

    public bulkDelete(request: ContasReceberBulkRequest): Observable<ContasReceberBulkDeleteResponse> {
        const url = `${this.apiUrl}/bulk-delete`;
        return this.httpClient
            .delete<ContasReceberBulkDeleteResponse>(url, { body: request, ...this.addHeaderToken() })
            .pipe(catchError(this.handleError));
    }

    public bulkCancelar(request: ContasReceberBulkRequest): Observable<ContasReceberBulkDeleteResponse> {
        const url = `${this.apiUrl}/bulk-cancelar`;
        return this.httpClient
            .put<ContasReceberBulkDeleteResponse>(url, request, this.addHeaderToken())
            .pipe(catchError(this.handleError));
    }

    public simularParcelas(request: any): Observable<any> {
        const url = `${this.apiUrl}/simular-parcelas`;
        return this.httpClient
            .post<any>(url, request, this.addHeaderToken())
            .pipe(catchError(this.handleError));
    }

    public simularRecorrencia(request: any): Observable<any> {
        const url = `${this.apiUrl}/simular-recorrencia`;
        return this.httpClient
            .post<any>(url, request, this.addHeaderToken())
            .pipe(catchError(this.handleError));
    }

    public renegociarParcelas(request: any): Observable<ContasReceberBulkDeleteResponse> {
        const url = `${this.apiUrl}/renegociar`;
        return this.httpClient
            .post<ContasReceberBulkDeleteResponse>(url, request, this.addHeaderToken())
            .pipe(catchError(this.handleError));
    }

    public getValorRenegociacao(idContasReceber: number): Observable<any> {
        const url = `${this.apiUrl}/valor-renegociacao/${idContasReceber}`;
        return this.httpClient
            .get<any>(url, this.addHeaderToken())
            .pipe(catchError(this.handleError));
    }

    public getArquivoContasReceber(urlArquivo: string): Observable<any> {
        const params = new HttpParams().set('urlArquivo', urlArquivo);
        const url = `${this.apiUrl}/getArquivoContasReceber`;
        return this.httpClient
            .get<any>(url, { params, ...this.addHeaderToken() })
            .pipe(catchError(this.handleError));
    }

    public validarAgrupamento(request: { idsParcelas: number[] }): Observable<any> {
        const url = `${this.apiUrl}/validar-agrupamento`;
        return this.httpClient
            .post<any>(url, request, this.addHeaderToken())
            .pipe(catchError(this.handleError));
    }

    public agrupar(request: any): Observable<ContasReceberBulkDeleteResponse> {
        const url = `${this.apiUrl}/agrupar`;
        return this.httpClient
            .post<ContasReceberBulkDeleteResponse>(url, request, this.addHeaderToken())
            .pipe(catchError(this.handleError));
    }

    public desagrupar(idContasReceberConsolidado: number): Observable<ContasReceberBulkDeleteResponse> {
        const url = `${this.apiUrl}/desagrupar/${idContasReceberConsolidado}`;
        return this.httpClient
            .post<ContasReceberBulkDeleteResponse>(url, {}, this.addHeaderToken())
            .pipe(catchError(this.handleError));
    }

    public editarEmMassa(request: ContasReceberBulkEditRequest): Observable<ContasReceberBulkDeleteResponse> {
        const url = `${this.apiUrl}/bulk`;
        return this.httpClient
            .patch<ContasReceberBulkDeleteResponse>(url, request, this.addHeaderToken())
            .pipe(catchError(this.handleError));
    }

    public alterarCategoriaEmMassa(request: ContasReceberBulkCategoriaRequest): Observable<ReturnStruct> {
        const url = `${this.apiUrl}/bulk-categoria`;
        return this.httpClient
            .patch<ReturnStruct>(url, request, this.addHeaderToken())
            .pipe(catchError(this.handleError));
    }

    public alterarTagsEmMassa(request: ContasReceberBulkTagsRequest): Observable<ReturnStruct> {
        const url = `${this.apiUrl}/bulk-tags`;
        return this.httpClient
            .patch<ReturnStruct>(url, request, this.addHeaderToken())
            .pipe(catchError(this.handleError));
    }

    public estornarRecebimento(idContasReceberParcela: number): Observable<ReturnStruct> {
        const url = `${this.apiUrl}/estorno`;
        return this.httpClient
            .post<ReturnStruct>(url, { idContasReceberParcela }, this.addHeaderToken())
            .pipe(catchError(this.handleError));
    }

    public bulkAlterarFormaRecebimento(request: ContasReceberBulkAlterarFormaRecebimentoRequest): Observable<ContasReceberBulkDeleteResponse> {
        const url = `${this.apiUrl}/bulk-forma-recebimento`;
        return this.httpClient
            .patch<ContasReceberBulkDeleteResponse>(url, request, this.addHeaderToken())
            .pipe(catchError(this.handleError));
    }

    public marcarPerda(request: MarcarPerdaContasReceberRequest): Observable<ContasReceberBulkDeleteResponse> {
        const url = `${this.apiUrl}/marcar-perda/${request.idContasReceberParcela}`;
        return this.httpClient
            .post<ContasReceberBulkDeleteResponse>(url, {}, this.addHeaderToken())
            .pipe(catchError(this.handleError));
    }

    public reverterPerda(request: ReverterPerdaContasReceberRequest): Observable<ReturnStruct> {
        const url = `${this.apiUrl}/reverter-perda/${request.idContasReceberParcela}`;
        return this.httpClient
            .post<ReturnStruct>(url, {}, this.addHeaderToken())
            .pipe(catchError(this.handleError));
    }

    public getEstornoPixInfo(idContasReceberParcela: number): Observable<EstornoPixInfoResponse> {
        const url = `${this.apiUrl}/estorno-pix-info/${idContasReceberParcela}`;
        return this.httpClient
            .get<EstornoPixInfoResponse>(url, this.addHeaderToken())
            .pipe(catchError(this.handleError.bind(this)));
    }

    public getCobrancaAtivoInfo(idsParcelas: number[]): Observable<CobrancaAtivoInfoResponse> {
        const url = `${this.apiUrl}/cobranca-ativo-info`;
        return this.httpClient
            .post<CobrancaAtivoInfoResponse>(url, idsParcelas, this.addHeaderToken())
            .pipe(catchError(this.handleError.bind(this)));
    }

    /** Baixa manual de boletos REGISTRADOS das parcelas (individual ou massa). Não altera a parcela. */
    public solicitarBaixaBoletos(idsParcelas: number[]): Observable<ContasReceberBulkDeleteResponse> {
        const url = `${environment.urlApiFinancas}IntegracaoBoleto/solicitar-baixa-parcelas`;
        return this.httpClient
            .post<ContasReceberBulkDeleteResponse>(url, idsParcelas, this.addHeaderToken())
            .pipe(catchError(this.handleError.bind(this)));
    }
}
